import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  TrendingUp, Upload, ListChecks, Scale, Check, Wand2, RefreshCw, FileText
} from 'lucide-react';
import ApiService, {
  CeConto, CeMovimento, CeCategoria, CeSuggerimento, CeEsitoImport, CeEsitoDuplicati,
  CeEsitoDeleghe, CeEsitoAmazon, CeEsitoQuietanze,
  ContoEconomico, CeRiconciliazione, VoceContoEconomico, SezioneCe
} from '../services/api';
import {
  PageContainerWide, PageHeader, Button, Card, CardHeader, CardBody,
  Alert, Badge, FormGroup, Label, Input, Select, LoadingState
} from '../components/ui';
import FattureTab from '../components/FattureTab';
import RegoleTab from '../components/RegoleTab';

// ============================================================
// Formattazione
// ============================================================
const euro = (valore: number | string | null | undefined): string => {
  const numero = Number(valore);
  if (!Number.isFinite(numero)) return '—';
  return numero.toLocaleString('it-IT', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2
  });
};

const percentuale = (valore: number | null | undefined): string =>
  valore === null || valore === undefined || !Number.isFinite(valore)
    ? '—'
    : `${valore.toFixed(1)}%`;

const dataItaliana = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('it-IT') : '—';

/** Primo e ultimo giorno dell'anno in corso, come default sensato. */
const annoCorrente = () => {
  const anno = new Date().getFullYear();
  return { da: `${anno}-01-01`, a: `${anno}-12-31` };
};

// ============================================================
// Styled components locali (composizione: non hanno un
// equivalente fra i primitivi ui/)
// ============================================================
const Tabs = styled.div`
  display: flex;
  gap: var(--space-sm);
  border-bottom: 2px solid var(--gray-200);
  margin-bottom: var(--space-lg);
  overflow-x: auto;
`;

const Tab = styled.button<{ $attiva: boolean }>`
  background: none;
  border: none;
  border-bottom: 3px solid ${(p) => (p.$attiva ? 'var(--primary-600)' : 'transparent')};
  color: ${(p) => (p.$attiva ? 'var(--primary-700)' : 'var(--text-secondary)')};
  font-weight: ${(p) => (p.$attiva ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)')};
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  padding: var(--space-sm) var(--space-lg);
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin-bottom: -2px;

  &:hover { color: var(--primary-700); }
`;

/** Card con la spaziatura verticale della pagina. */
const Sezione = styled(Card)`
  margin-bottom: var(--space-lg);
`;

const Filtri = styled.div`
  display: flex;
  gap: var(--space-md);
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: var(--space-lg);

  ${FormGroup} {
    margin-bottom: 0;
  }
`;

const TabellaScorrevole = styled.div`
  overflow-x: auto;
`;

const Tabella = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);

  th {
    text-align: left;
    padding: var(--space-sm) var(--space-md);
    border-bottom: 2px solid var(--gray-200);
    color: var(--text-secondary);
    font-weight: var(--font-weight-semibold);
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  td {
    padding: var(--space-sm) var(--space-md);
    border-bottom: 1px solid var(--gray-100);
    vertical-align: middle;
  }

  tbody tr:hover { background: var(--gray-50); }
`;

const Numero = styled.td<{ $negativo?: boolean }>`
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${(p) => (p.$negativo ? 'var(--error)' : 'var(--text-primary)')};
`;

const RigaSezione = styled.tr`
  td {
    padding-top: var(--space-lg);
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-light);
    font-weight: var(--font-weight-bold);
    border-bottom: none;
  }
`;

const RigaMargine = styled.tr<{ $forte?: boolean }>`
  td {
    font-weight: var(--font-weight-bold);
    background: ${(p) => (p.$forte ? 'var(--primary-50)' : 'var(--gray-50)')};
    border-top: 2px solid ${(p) => (p.$forte ? 'var(--primary-600)' : 'var(--gray-300)')};
    border-bottom: none;
    font-size: ${(p) => (p.$forte ? 'var(--font-size-base)' : 'var(--font-size-sm)')};
  }
`;

/** Testo di spiegazione adiacente a una tabella o a un form. */
const Nota = styled.p`
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.5;
  margin: var(--space-sm) 0 0;
`;

const Vuoto = styled.div`
  text-align: center;
  padding: var(--space-2xl) var(--space-md);
  color: var(--text-light);
`;

// ============================================================
// Conto economico
// ============================================================
const SEZIONI_ORDINE: { chiave: SezioneCe; titolo: string }[] = [
  { chiave: 'ricavo', titolo: 'Ricavi' },
  { chiave: 'costo_variabile', titolo: 'Costi variabili' },
  { chiave: 'costo_fisso', titolo: 'Costi fissi' },
  { chiave: 'sotto_linea', titolo: 'Oneri finanziari e imposte' }
];

const ContoEconomicoTab: React.FC = () => {
  const [periodo, setPeriodo] = useState(annoCorrente());
  const [base, setBase] = useState<'cassa' | 'competenza'>('cassa');
  const [dati, setDati] = useState<ContoEconomico | null>(null);
  const [caricamento, setCaricamento] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const carica = useCallback(async () => {
    setCaricamento(true);
    setErrore(null);
    try {
      setDati(await ApiService.getContoEconomico(periodo.da, periodo.a, base));
    } catch (e: any) {
      setErrore(e?.response?.data?.error || 'Errore nel calcolo del conto economico');
    } finally {
      setCaricamento(false);
    }
  }, [periodo, base]);

  useEffect(() => { carica(); }, [carica]);

  const incidenza = (valore: number) =>
    dati && dati.totali.ricavi > 0 ? percentuale((valore / dati.totali.ricavi) * 100) : '—';

  const rigaVoce = (voce: VoceContoEconomico) => (
    <tr key={voce.codice}>
      <td>
        {voce.nome}
        {voce.fonte === 'kiara' && (
          <> <Badge $variant="info">Kiara</Badge></>
        )}
      </td>
      <Numero $negativo={voce.totale < 0}>{euro(voce.totale)}</Numero>
      <Numero>{incidenza(voce.totale)}</Numero>
      <Numero>{voce.movimenti ?? '—'}</Numero>
    </tr>
  );

  return (
    <>
      <Filtri>
        <FormGroup>
          <Label htmlFor="ce-da">Dal</Label>
          <Input id="ce-da" type="date" value={periodo.da}
            onChange={(e) => setPeriodo({ ...periodo, da: e.target.value })} />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="ce-a">Al</Label>
          <Input id="ce-a" type="date" value={periodo.a}
            onChange={(e) => setPeriodo({ ...periodo, a: e.target.value })} />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="ce-base">Rilevazione</Label>
          <Select id="ce-base" value={base} onChange={(e) => setBase(e.target.value as 'cassa' | 'competenza')}>
            <option value="cassa">Per cassa</option>
            <option value="competenza">Per competenza</option>
          </Select>
        </FormGroup>
        <Button $variant="secondary" onClick={carica} disabled={caricamento}>
          <RefreshCw /> {caricamento ? 'Calcolo…' : 'Aggiorna'}
        </Button>
      </Filtri>

      {errore && <Alert $variant="error">{errore}</Alert>}

      {dati && dati.daClassificare.movimenti > 0 && (
        <Alert $variant="warning">
          <strong>Il quadro non è completo.</strong> Nel periodo ci sono{' '}
          {dati.daClassificare.movimenti} movimenti ancora da classificare, per{' '}
          {euro(dati.daClassificare.valoreAssoluto)} in valore assoluto: non entrano in
          nessuna voce e quindi non concorrono ai margini qui sotto.
        </Alert>
      )}

      {dati && dati.totali.ricavi === 0 && (
        <Alert $variant="warning">
          Nel periodo non risultano ricavi. Le percentuali di incidenza non hanno
          significato senza un fatturato con cui confrontarsi.
        </Alert>
      )}

      <Sezione>
        <CardBody>
          {!dati && caricamento && <LoadingState label="Calcolo in corso…" />}
          {dati && (
            <TabellaScorrevole>
              <Tabella>
                <thead>
                  <tr>
                    <th>Voce</th>
                    <th style={{ textAlign: 'right' }}>Importo</th>
                    <th style={{ textAlign: 'right' }}>Su ricavi</th>
                    <th style={{ textAlign: 'right' }}>Movimenti</th>
                  </tr>
                </thead>
                <tbody>
                  {SEZIONI_ORDINE.map(({ chiave, titolo }) => {
                    const voci = dati.sezioni[chiave] || [];
                    if (!voci.length) return null;
                    return (
                      <React.Fragment key={chiave}>
                        <RigaSezione><td colSpan={4}>{titolo}</td></RigaSezione>
                        {voci.map(rigaVoce)}

                        {chiave === 'costo_variabile' && (
                          <RigaMargine>
                            <td>Margine di contribuzione</td>
                            <Numero $negativo={dati.totali.margineContribuzione < 0}>
                              {euro(dati.totali.margineContribuzione)}
                            </Numero>
                            <Numero>{percentuale(dati.totali.margineContribuzionePercentuale)}</Numero>
                            <td />
                          </RigaMargine>
                        )}

                        {chiave === 'costo_fisso' && (
                          <RigaMargine $forte>
                            <td>EBITDA</td>
                            <Numero $negativo={dati.totali.ebitda < 0}>{euro(dati.totali.ebitda)}</Numero>
                            <Numero>{percentuale(dati.totali.ebitdaPercentuale)}</Numero>
                            <td />
                          </RigaMargine>
                        )}
                      </React.Fragment>
                    );
                  })}

                  <RigaMargine $forte>
                    <td>Risultato netto</td>
                    <Numero $negativo={dati.totali.risultatoNetto < 0}>
                      {euro(dati.totali.risultatoNetto)}
                    </Numero>
                    <Numero>{incidenza(dati.totali.risultatoNetto)}</Numero>
                    <td />
                  </RigaMargine>
                </tbody>
              </Tabella>
            </TabellaScorrevole>
          )}
        </CardBody>
      </Sezione>

      {dati && dati.sezioni.finanziario?.length > 0 && (
        <Sezione>
          <CardHeader><h3>Movimenti finanziari</h3></CardHeader>
          <CardBody>
            <Nota style={{ margin: '0 0 var(--space-md)' }}>
              Non sono né ricavi né costi e restano fuori dai margini: versamenti di contante,
              giroconti, quota capitale dei mutui e accrediti degli incassi, che rappresentano
              denaro già contato fra i ricavi Kiara.
            </Nota>
            <TabellaScorrevole>
              <Tabella>
                <tbody>
                  {dati.sezioni.finanziario.map((voce) => (
                    <tr key={voce.codice}>
                      <td>{voce.nome}</td>
                      <Numero $negativo={voce.totale < 0}>{euro(voce.totale)}</Numero>
                      <Numero>{voce.movimenti ?? '—'}</Numero>
                    </tr>
                  ))}
                </tbody>
              </Tabella>
            </TabellaScorrevole>
          </CardBody>
        </Sezione>
      )}
    </>
  );
};

// ============================================================
// Coda di classificazione
// ============================================================
const CodaTab: React.FC = () => {
  const [movimenti, setMovimenti] = useState<CeMovimento[]>([]);
  const [totale, setTotale] = useState(0);
  const [categorie, setCategorie] = useState<CeCategoria[]>([]);
  const [caricamento, setCaricamento] = useState(false);
  const [selezionato, setSelezionato] = useState<CeMovimento | null>(null);
  const [suggerimenti, setSuggerimenti] = useState<CeSuggerimento[]>([]);
  const [categoriaScelta, setCategoriaScelta] = useState<number | ''>('');
  const [creaRegola, setCreaRegola] = useState(false);
  const [patternRegola, setPatternRegola] = useState('');
  const [messaggio, setMessaggio] = useState<string | null>(null);

  const carica = useCallback(async () => {
    setCaricamento(true);
    try {
      const [esito, cats] = await Promise.all([
        ApiService.getCeMovimenti({ stato: 'da_classificare', limite: 100 }),
        ApiService.getCeCategorie()
      ]);
      setMovimenti(esito.movimenti);
      setTotale(esito.totale);
      setCategorie(cats);
    } finally {
      setCaricamento(false);
    }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const apri = async (movimento: CeMovimento) => {
    setSelezionato(movimento);
    setCategoriaScelta('');
    setCreaRegola(false);
    // Il pattern proposto è la descrizione ripulita dai numeri lunghi, che
    // cambiano a ogni operazione e renderebbero la regola inutile.
    setPatternRegola(movimento.descrizione.replace(/\d{5,}/g, '').replace(/\s+/g, ' ').trim().slice(0, 40));
    setSuggerimenti([]);
    try {
      setSuggerimenti(await ApiService.getCeSuggerimenti(movimento.id));
    } catch {
      // I suggerimenti sono un aiuto: se mancano si classifica comunque a mano
    }
  };

  const salva = async () => {
    if (!selezionato || categoriaScelta === '') return;
    setMessaggio(null);
    try {
      const esito = await ApiService.classificaCeMovimento(selezionato.id, {
        categoriaId: Number(categoriaScelta),
        creaRegola,
        patternRegola: creaRegola ? patternRegola : undefined
      });
      setMessaggio(
        esito.regolaCreata
          ? 'Movimento classificato e regola creata: i movimenti simili non torneranno in coda.'
          : 'Movimento classificato.'
      );
      setSelezionato(null);
      await carica();
    } catch (e: any) {
      setMessaggio(e?.response?.data?.error || 'Errore nella classificazione');
    }
  };

  const riapplica = async () => {
    const esito = await ApiService.riclassificaCeMovimenti();
    setMessaggio(`Regole riapplicate: ${esito.classificati} movimenti classificati su ${esito.esaminati} esaminati.`);
    await carica();
  };

  const categoriePerSezione = useMemo(() => {
    const gruppi = new Map<string, CeCategoria[]>();
    for (const categoria of categorie) {
      const elenco = gruppi.get(categoria.sezione) || [];
      elenco.push(categoria);
      gruppi.set(categoria.sezione, elenco);
    }
    return gruppi;
  }, [categorie]);

  return (
    <>
      <Filtri>
        <div style={{ flex: 1 }}>
          <strong>{totale}</strong> movimenti da classificare
        </div>
        <Button $variant="secondary" onClick={riapplica}>
          <Wand2 /> Riapplica le regole
        </Button>
      </Filtri>

      {messaggio && <Alert $variant="info">{messaggio}</Alert>}

      {selezionato && (
        <Sezione>
          <CardHeader><h3>Classifica movimento</h3></CardHeader>
          <CardBody>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)', marginTop: 0 }}>
              <strong>{dataItaliana(selezionato.data_operazione)}</strong> · {euro(selezionato.importo)}
              <br />
              <span style={{ color: 'var(--text-secondary)' }}>{selezionato.descrizione}</span>
            </p>

            {suggerimenti.length > 0 && (
              <div style={{ marginBottom: 'var(--space-md)' }}>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)', marginBottom: 'var(--space-xs)' }}>
                  Movimenti simili già classificati:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-sm)' }}>
                  {suggerimenti.map((s) => (
                    <Button key={s.categoriaId} $variant="secondary" $size="sm"
                      onClick={() => setCategoriaScelta(s.categoriaId)}>
                      {s.categoriaNome} <span style={{ color: 'var(--text-light)' }}>({s.occorrenze})</span>
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <Filtri>
              <FormGroup style={{ minWidth: '260px' }}>
                <Label htmlFor="cat">Categoria</Label>
                <Select id="cat" value={categoriaScelta}
                  onChange={(e) => setCategoriaScelta(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">Scegli una voce…</option>
                  {SEZIONI_ORDINE.concat([{ chiave: 'finanziario', titolo: 'Movimenti finanziari' }])
                    .map(({ chiave, titolo }) => (
                      <optgroup key={chiave} label={titolo}>
                        {(categoriePerSezione.get(chiave) || []).map((c) => (
                          <option key={c.id} value={c.id}>{c.nome}</option>
                        ))}
                      </optgroup>
                    ))}
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>
                  <input type="checkbox" checked={creaRegola}
                    onChange={(e) => setCreaRegola(e.target.checked)} />{' '}
                  Crea una regola
                </Label>
                {creaRegola && (
                  <Input value={patternRegola} onChange={(e) => setPatternRegola(e.target.value)}
                    placeholder="Testo da riconoscere" style={{ minWidth: '240px' }} />
                )}
              </FormGroup>
              <Button $variant="primary" onClick={salva} disabled={categoriaScelta === ''}>
                <Check /> Salva
              </Button>
              <Button $variant="secondary" onClick={() => setSelezionato(null)}>Annulla</Button>
            </Filtri>

            {creaRegola && (
              <Alert $variant="warning">
                La regola si applicherà a tutti i movimenti futuri la cui descrizione contiene
                questo testo. Tieni solo la parte che identifica il fornitore: i numeri
                d'operazione cambiano ogni volta e renderebbero la regola inutile.
              </Alert>
            )}
          </CardBody>
        </Sezione>
      )}

      <Sezione>
        <CardBody>
          {caricamento && <LoadingState label="Caricamento…" />}
          {!caricamento && !movimenti.length && (
            <Vuoto>
              <Check size={32} style={{ opacity: 0.4 }} />
              <p>Nessun movimento in coda: è tutto classificato.</p>
            </Vuoto>
          )}
          {!!movimenti.length && (
            <TabellaScorrevole>
              <Tabella>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Descrizione</th>
                    <th>Conto</th>
                    <th style={{ textAlign: 'right' }}>Importo</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {movimenti.map((m) => (
                    <tr key={m.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{dataItaliana(m.data_operazione)}</td>
                      <td>{m.descrizione}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{m.conto_nome}</td>
                      <Numero $negativo={Number(m.importo) < 0}>{euro(m.importo)}</Numero>
                      <td style={{ textAlign: 'right' }}>
                        <Button $variant="secondary" $size="sm" onClick={() => apri(m)}>Classifica</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Tabella>
            </TabellaScorrevole>
          )}
        </CardBody>
      </Sezione>
    </>
  );
};

// ============================================================
// Import estratti conto
// ============================================================
/**
 * Creazione di un conto.
 *
 * Senza almeno un conto la tendina dell'import resta vuota e non c'e' modo di
 * caricare un estratto conto: e' il primo passo di un'installazione nuova.
 */
const NuovoConto: React.FC<{ onCreato: () => void }> = ({ onCreato }) => {
  const [nome, setNome] = useState('');
  const [formato, setFormato] = useState('sella_conto');
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const crea = async () => {
    if (!nome.trim()) return;
    setInCorso(true);
    setErrore(null);
    try {
      await ApiService.creaCeConto({
        nome: nome.trim(),
        tipo: formato === 'sella_carta' ? 'carta_credito' : 'conto_corrente',
        formatoImport: formato
      });
      setNome('');
      onCreato();
    } catch (e: any) {
      setErrore(e?.response?.data?.error || 'Errore nella creazione del conto');
    } finally {
      setInCorso(false);
    }
  };

  return (
    <>
      <Filtri>
        <FormGroup style={{ minWidth: '240px' }}>
          <Label htmlFor="nuovo-conto-nome">Nome del conto</Label>
          <Input id="nuovo-conto-nome" value={nome} placeholder="Banca Sella conto corrente"
            onChange={(e) => setNome(e.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="nuovo-conto-formato">Formato dell'estratto</Label>
          <Select id="nuovo-conto-formato" value={formato}
            onChange={(e) => setFormato(e.target.value)}>
            <option value="sella_conto">Sella — lista movimenti conto</option>
            <option value="sella_carta">Sella — lista movimenti carta</option>
          </Select>
        </FormGroup>
        <Button $variant="primary" onClick={crea} disabled={!nome.trim() || inCorso}>
          {inCorso ? 'Creazione…' : 'Crea conto'}
        </Button>
      </Filtri>
      {errore && <Alert $variant="error">{errore}</Alert>}
    </>
  );
};

/**
 * I doppioni gia' caricati.
 *
 * L'export "movimenti carta" di Banca Sella contiene anche la carta di debito,
 * le cui spese stanno gia' una per una nell'estratto conto. Da ora l'import se
 * ne accorge; quello che era stato caricato prima va ripulito una volta.
 */
const Duplicati: React.FC = () => {
  const [esito, setEsito] = useState<CeEsitoDuplicati | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const cerca = async (applica: boolean) => {
    setInCorso(true);
    setErrore(null);
    try {
      setEsito(await ApiService.duplicatiFraConti(applica));
    } catch (e: any) {
      setErrore(e?.response?.data?.error || 'Errore nella ricerca dei duplicati');
    } finally {
      setInCorso(false);
    }
  };

  return (
    <>
      <Filtri>
        <Button $variant="secondary" onClick={() => cerca(false)} disabled={inCorso}>
          Cerca doppioni fra i conti
        </Button>
        {esito && esito.soloAnteprima && esito.esclusi > 0 && (
          <Button $variant="primary" onClick={() => cerca(true)} disabled={inCorso}>
            Escludi {esito.esclusi} righe
          </Button>
        )}
      </Filtri>

      {errore && <Alert $variant="error">{errore}</Alert>}

      {esito && esito.esclusi === 0 && (
        <Alert $variant="success">Nessun pagamento risulta caricato due volte.</Alert>
      )}

      {esito && esito.esclusi > 0 && (
        <Alert $variant={esito.soloAnteprima ? 'warning' : 'success'}>
          {esito.soloAnteprima
            ? <><strong>{esito.esclusi}</strong> righe descrivono pagamenti già presenti su un
                altro conto, per <strong>{euro(esito.valoreEscluso)}</strong>. Sono spese contate
                due volte: succede caricando l'export della carta, che contiene anche le spese
                che l'estratto conto riporta già una per una. Escludendole restano quelle del
                conto corrente, e niente viene cancellato.</>
            : <><strong>{esito.esclusi}</strong> righe escluse, per {euro(esito.valoreEscluso)}.
                Restano visibili nell'elenco movimenti, marcate come escluse.</>}
          {!!esito.esempi.length && (
            <ul style={{ margin: 'var(--space-sm) 0 0', paddingLeft: '1.2rem' }}>
              {esito.esempi.slice(0, 6).map((e, i) => (
                <li key={i}>
                  {dataItaliana(e.data)} · {e.descrizione} · {euro(e.importo)} ({e.conto})
                </li>
              ))}
            </ul>
          )}
        </Alert>
      )}
    </>
  );
};

const ImportTab: React.FC = () => {
  const [conti, setConti] = useState<CeConto[]>([]);
  const [contoScelto, setContoScelto] = useState<number | ''>('');
  const [files, setFiles] = useState<File[]>([]);
  const [esito, setEsito] = useState<CeEsitoImport | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [storico, setStorico] = useState<any[]>([]);
  const [creaConto, setCreaConto] = useState(false);

  const carica = useCallback(async () => {
    const [c, st] = await Promise.all([ApiService.getCeConti(), ApiService.getCeStoricoImport()]);
    setConti(c);
    setStorico(st);
    setContoScelto((precedente) => (precedente === '' && c.length ? c[0].id : precedente));
    if (c.length) setCreaConto(false);
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const importa = async () => {
    if (!files.length || contoScelto === '') return;
    setInCorso(true);
    setErrore(null);
    setEsito(null);
    try {
      setEsito(await ApiService.importaEstrattoConto(Number(contoScelto), files));
      setFiles([]);
      await carica();
    } catch (e: any) {
      setErrore(e?.response?.data?.error || "Errore nell'import dei file");
    } finally {
      setInCorso(false);
    }
  };

  return (
    <>
      <Alert $variant="info">
        Puoi selezionare più file insieme: vengono elaborati uno alla volta e ognuno ha il
        suo esito, così un file con problemi non blocca gli altri. Ricaricare un estratto
        conto già importato è innocuo — i movimenti già presenti vengono riconosciuti e non
        duplicati, quindi le sovrapposizioni fra export non creano problemi.
      </Alert>

      <Sezione>
        <CardBody>
          {!conti.length && (
            <Alert $variant="warning">
              Non c'è ancora nessun conto configurato: creane uno qui sotto indicando il
              formato dell'export della banca, poi potrai caricare gli estratti conto.
            </Alert>
          )}

          {(!conti.length || creaConto) && <NuovoConto onCreato={carica} />}

          <Filtri>
            <FormGroup style={{ minWidth: '240px' }}>
              <Label htmlFor="conto">Conto</Label>
              <Select id="conto" value={contoScelto} disabled={!conti.length}
                onChange={(e) => setContoScelto(e.target.value ? Number(e.target.value) : '')}>
                {!conti.length && <option value="">Nessun conto configurato</option>}
                {conti.map((c) => (
                  <option key={c.id} value={c.id}>{c.nome} · {c.movimenti} movimenti</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label htmlFor="file">File CSV</Label>
              <Input id="file" type="file" accept=".csv,text/csv" multiple
                onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            </FormGroup>
            <Button $variant="primary" onClick={importa}
              disabled={!files.length || inCorso || contoScelto === ''}>
              <Upload />
              {inCorso ? 'Import in corso…' : `Importa${files.length > 1 ? ` ${files.length} file` : ''}`}
            </Button>
            {conti.length > 0 && !creaConto && (
              <Button $variant="secondary" onClick={() => setCreaConto(true)}>
                Aggiungi conto
              </Button>
            )}
          </Filtri>

          <Duplicati />

          {files.length > 1 && !inCorso && (
            <Nota>
              {files.length} file selezionati, verranno elaborati in sequenza.
            </Nota>
          )}

          {errore && <Alert $variant="error">{errore}</Alert>}

          {esito && (
            <>
              <Alert $variant="success">
                {esito.file} file elaborati: <strong>{esito.totali.righeLette}</strong> righe
                lette, <strong>{esito.totali.importate}</strong> nuove, di cui{' '}
                <strong>{esito.totali.classificate}</strong> classificate automaticamente e{' '}
                <strong>{esito.totali.daClassificare}</strong> finite in coda.
                {esito.totali.duplicate > 0 && <> {esito.totali.duplicate} già presenti, ignorate.</>}
                {esito.totali.giaSuAltroConto > 0 && (
                  <> <strong>{esito.totali.giaSuAltroConto}</strong> righe descrivono pagamenti
                    che l'estratto conto riporta già: non sono state ricaricate, altrimenti la
                    stessa spesa risulterebbe due volte.</>
                )}
                {esito.totali.sostituiti > 0 && (
                  <> {esito.totali.sostituiti} righe della carta sono state escluse perché la
                    stessa spesa è arrivata dal conto corrente.</>
                )}
              </Alert>

              <TabellaScorrevole>
                <Tabella>
                  <thead>
                    <tr>
                      <th>File</th><th>Periodo</th>
                      <th style={{ textAlign: 'right' }}>Lette</th>
                      <th style={{ textAlign: 'right' }}>Nuove</th>
                      <th style={{ textAlign: 'right' }}>Duplicate</th>
                      <th style={{ textAlign: 'right' }}>In coda</th>
                    </tr>
                  </thead>
                  <tbody>
                    {esito.perFile.map((r) => (
                      <tr key={r.file}>
                        <td>{r.file}</td>
                        <td style={{ whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                          {dataItaliana(r.esito.periodo.da)} – {dataItaliana(r.esito.periodo.a)}
                        </td>
                        <Numero>{r.esito.righeLette}</Numero>
                        <Numero>{r.esito.importate}</Numero>
                        <Numero>{r.esito.duplicate}</Numero>
                        <Numero>{r.esito.daClassificare}</Numero>
                      </tr>
                    ))}
                  </tbody>
                </Tabella>
              </TabellaScorrevole>

              {!!esito.spostati?.length && (
                <Alert $variant="info">
                  <strong>
                    {esito.spostati.length} file {esito.spostati.length === 1 ? 'era' : 'erano'} di
                    un altro formato
                  </strong>{' '}
                  e {esito.spostati.length === 1 ? 'è stato portato' : 'sono stati portati'} sul
                  conto giusto: il formato lo dice il file, non la tendina.
                  <ul style={{ margin: 'var(--space-sm) 0 0', paddingLeft: '1.2rem' }}>
                    {esito.spostati.map((f) => (
                      <li key={f.file}>
                        {f.file}: {f.importate} movimenti su <strong>{f.a}</strong> invece che su {f.da}
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}

              {!!esito.falliti.length && (
                <Alert $variant="warning">
                  <strong>{esito.falliti.length} file non importati.</strong> Gli altri sono
                  entrati regolarmente.
                  <ul style={{ margin: 'var(--space-sm) 0 0', paddingLeft: '1.2rem' }}>
                    {esito.falliti.map((f) => <li key={f.file}>{f.file}: {f.errore}</li>)}
                  </ul>
                </Alert>
              )}
            </>
          )}
        </CardBody>
      </Sezione>

      <AltriImport />

      <Sezione>
        <CardHeader><h3>Import precedenti</h3></CardHeader>
        <CardBody>
          {!storico.length && <Vuoto>Nessun import registrato.</Vuoto>}
          {!!storico.length && (
            <TabellaScorrevole>
              <Tabella>
                <thead>
                  <tr>
                    <th>Data</th><th>File</th><th>Conto</th>
                    <th style={{ textAlign: 'right' }}>Importate</th>
                    <th style={{ textAlign: 'right' }}>Duplicate</th>
                    <th>Esito</th>
                  </tr>
                </thead>
                <tbody>
                  {storico.map((r) => (
                    <tr key={r.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{dataItaliana(r.created_at)}</td>
                      <td style={{ maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.nome_file}
                      </td>
                      <td>{r.conto_nome}</td>
                      <Numero>{r.righe_importate}</Numero>
                      <Numero>{r.righe_duplicate}</Numero>
                      <td>
                        <Badge $variant={r.stato === 'completato' ? 'success' : 'error'}>
                          {r.stato}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Tabella>
            </TabellaScorrevole>
          )}
        </CardBody>
      </Sezione>
    </>
  );
};

// ============================================================
// Deleghe F24 e ordini Amazon
// ============================================================
const NOMI_NATURA: Record<string, string> = {
  iva: 'IVA',
  contributi_amministratore: 'Contributi INPS amministratore',
  ritenute: 'Ritenute',
  imposte_societa: 'Imposte sulla società',
  sanzioni: 'Sanzioni e interessi',
  da_classificare: 'Codici tributo non riconosciuti'
};

const NOMI_CATEGORIA: Record<string, string> = {
  VAR_INGREDIENTI: 'Ingredienti',
  VAR_INGR_EXTRA: 'Ingredienti extra',
  FIS_ATTREZZATURE: 'Attrezzature e utensili',
  FIS_OPERATIVE: 'Altre spese operative',
  FIS_PERS_BENEFIT: 'Benefit al personale'
};

const AltriImport: React.FC = () => {
  const [fileF24, setFileF24] = useState<File[]>([]);
  const [cfAzienda, setCfAzienda] = useState('');
  const [esitoF24, setEsitoF24] = useState<CeEsitoDeleghe | null>(null);

  const [fileAmazon, setFileAmazon] = useState<File[]>([]);
  const [esitoAmazon, setEsitoAmazon] = useState<CeEsitoAmazon | null>(null);

  const [fileQuietanze, setFileQuietanze] = useState<File[]>([]);
  const [esitoQuietanze, setEsitoQuietanze] = useState<CeEsitoQuietanze | null>(null);

  const [inCorso, setInCorso] = useState<'f24' | 'amazon' | 'quietanze' | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  const importaF24 = async () => {
    if (!fileF24.length) return;
    setInCorso('f24'); setErrore(null); setEsitoF24(null);
    try {
      setEsitoF24(await ApiService.importaDelegheF24(fileF24, cfAzienda || undefined));
      setFileF24([]);
    } catch (e: any) {
      setErrore(e?.response?.data?.error || "Errore nell'import delle deleghe");
    } finally { setInCorso(null); }
  };

  const analizzaQuietanze = async () => {
    if (!fileQuietanze.length) return;
    setInCorso('quietanze'); setErrore(null); setEsitoQuietanze(null);
    try {
      setEsitoQuietanze(await ApiService.analizzaQuietanzeF24(fileQuietanze));
      setFileQuietanze([]);
    } catch (e: any) {
      setErrore(e?.response?.data?.error || "Errore nella lettura delle quietanze");
    } finally { setInCorso(null); }
  };

  const analizzaAmazon = async () => {
    if (!fileAmazon.length) return;
    setInCorso('amazon'); setErrore(null); setEsitoAmazon(null);
    try {
      setEsitoAmazon(await ApiService.analizzaOrdiniAmazon(fileAmazon));
      setFileAmazon([]);
    } catch (e: any) {
      setErrore(e?.response?.data?.error || "Errore nell'analisi degli ordini");
    } finally { setInCorso(null); }
  };

  return (
    <>
      {errore && <Alert $variant="error">{errore}</Alert>}

      <Sezione>
        <CardHeader><h3>Deleghe F24</h3></CardHeader>
        <CardBody>
          <Nota style={{ margin: '0 0 var(--space-md)' }}>
            Sul conto corrente un F24 è solo “PAG. DELEGA F24”: non dice a chi sia intestato.
            L'elenco deleghe scaricato dalla banca porta il codice fiscale, e incrociandolo per
            data e importo si separano le imposte della società dai contributi intestati a una
            persona e dai versamenti fatti per conto di altre società.
          </Nota>
          <Filtri>
            <FormGroup>
              <Label htmlFor="cf">Codice fiscale della società</Label>
              <Input id="cf" value={cfAzienda} onChange={(e) => setCfAzienda(e.target.value)}
                placeholder="es. 06514670964" style={{ minWidth: '200px' }} />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="f24">Elenchi deleghe (CSV)</Label>
              <Input id="f24" type="file" accept=".csv,text/csv" multiple
                onChange={(e) => setFileF24(Array.from(e.target.files || []))} />
            </FormGroup>
            <Button $variant="primary" onClick={importaF24}
              disabled={!fileF24.length || inCorso !== null}>
              {inCorso === 'f24' ? 'Abbinamento…' : 'Abbina'}
            </Button>
          </Filtri>

          {!cfAzienda && (
            <Alert $variant="warning">
              Senza il codice fiscale della società non è possibile riconoscere le deleghe pagate
              per conto di altre società: verranno separati solo i contributi delle persone fisiche.
            </Alert>
          )}

          {esitoF24 && (
            <Alert $variant="success">
              {esitoF24.deleghe} deleghe lette da {esitoF24.file} file,{' '}
              <strong>{esitoF24.abbinati}</strong> abbinate ai movimenti bancari e{' '}
              <strong>{esitoF24.riclassificati}</strong> riclassificate perché non intestate
              alla società.
              {esitoF24.movimentiSenzaDelega > 0 && (
                <> Restano {esitoF24.movimentiSenzaDelega} movimenti F24 senza delega
                  corrispondente: servono gli elenchi dei periodi mancanti.</>
              )}
            </Alert>
          )}
        </CardBody>
      </Sezione>

      <Sezione>
        <CardHeader><h3>Quietanze F24</h3></CardHeader>
        <CardBody>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)', marginTop: 0 }}>
            L'elenco deleghe dice a chi è intestato un F24; le quietanze dicono cosa contiene.
            Serve soprattutto a separare i contributi previdenziali, che sono costo del lavoro,
            dall'IVA, che è solo un giro, e dalle imposte vere della società.
          </p>
          <Filtri>
            <FormGroup>
              <Label htmlFor="qtz">Quietanze (PDF)</Label>
              <Input id="qtz" type="file" accept="application/pdf" multiple
                onChange={(e) => setFileQuietanze(Array.from(e.target.files || []))} />
            </FormGroup>
            <Button $variant="primary" onClick={analizzaQuietanze}
              disabled={!fileQuietanze.length || inCorso !== null}>
              {inCorso === 'quietanze' ? 'Lettura…' : 'Leggi'}
            </Button>
          </Filtri>

          {esitoQuietanze && (
            <>
              <Alert $variant="success">
                <strong>{esitoQuietanze.quadrano}</strong> quietanze su {esitoQuietanze.lette}{' '}
                verificate: la somma dei codici tributo coincide col saldo versato riportato sul
                documento, quindi la lettura è corretta e non solo plausibile.
              </Alert>

              {!!esitoQuietanze.daVerificare.length && (
                <Alert $variant="warning">
                  <strong>{esitoQuietanze.daVerificare.length} quietanze non quadrano</strong> e
                  restano fuori dai totali qui sotto: i loro importi non sono verificati e
                  sommarli inquinerebbe il resto.
                  <ul style={{ margin: 'var(--space-sm) 0 0', paddingLeft: 'var(--space-lg)' }}>
                    {esitoQuietanze.daVerificare.map((q) => (
                      <li key={q.file}>
                        {q.file}: saldo {euro(q.saldoDelega)} contro {euro(q.sommaTributi)} letti
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}

              <TabellaScorrevole>
                <Tabella>
                  <thead>
                    <tr><th>Natura</th><th style={{ textAlign: 'right' }}>Totale</th><th>Codici tributo</th></tr>
                  </thead>
                  <tbody>
                    {esitoQuietanze.perNatura.map((n) => (
                      <tr key={n.natura}>
                        <td>{NOMI_NATURA[n.natura] || n.natura}</td>
                        <Numero>{euro(n.totale)}</Numero>
                        <td style={{ color: 'var(--text-light)', fontSize: 'var(--font-size-xs)' }}>
                          {n.codici.join(', ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Tabella>
              </TabellaScorrevole>

              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)',
                            margin: 'var(--space-lg) 0 var(--space-sm)' }}>
                Per intestatario
              </div>
              <TabellaScorrevole>
                <Tabella>
                  <tbody>
                    {esitoQuietanze.perIntestatario.map((i) => (
                      <tr key={i.codiceFiscale + i.denominazione}>
                        <td>
                          {i.denominazione || i.codiceFiscale}
                          {i.personaFisica && (
                            <> <Badge $variant="warning">persona fisica</Badge></>
                          )}
                        </td>
                        <Numero>{euro(i.totale)}</Numero>
                        <Numero style={{ color: 'var(--text-light)' }}>{i.quietanze} quietanze</Numero>
                      </tr>
                    ))}
                  </tbody>
                </Tabella>
              </TabellaScorrevole>
            </>
          )}
        </CardBody>
      </Sezione>

      <Sezione>
        <CardHeader><h3>Ordini Amazon</h3></CardHeader>
        <CardBody>
          <Nota style={{ margin: '0 0 var(--space-md)' }}>
            Sull'estratto conto Amazon è un unico addebito che non dice cosa contenga. L'export
            Business porta la categoria merceologica riga per riga: da lì si vede quanto sono
            ingredienti, quanto attrezzature e quanto benefit. L'analisi non modifica i
            movimenti, serve a decidere come attribuirli.
          </Nota>
          <Filtri>
            <FormGroup>
              <Label htmlFor="amz">Export ordini (CSV)</Label>
              <Input id="amz" type="file" accept=".csv,text/csv" multiple
                onChange={(e) => setFileAmazon(Array.from(e.target.files || []))} />
            </FormGroup>
            <Button $variant="primary" onClick={analizzaAmazon}
              disabled={!fileAmazon.length || inCorso !== null}>
              {inCorso === 'amazon' ? 'Analisi…' : 'Analizza'}
            </Button>
          </Filtri>

          {esitoAmazon && (
            <>
              <Alert $variant="success">
                <strong>{esitoAmazon.ordini}</strong> ordini distinti per{' '}
                <strong>{euro(esitoAmazon.totale)}</strong>.{' '}
                {esitoAmazon.conCategoriaRiconosciuta} con categoria riconosciuta.
                {esitoAmazon.ripartizioneStimata > 0 && (
                  <> {esitoAmazon.ripartizioneStimata} ordini contengono articoli di categorie
                    diverse: il totale è diviso in parti uguali, quindi è una stima e non un dato.</>
                )}
              </Alert>

              <TabellaScorrevole>
                <Tabella>
                  <tbody>
                    {esitoAmazon.perCategoria.map((c) => (
                      <tr key={c.codiceCategoria}>
                        <td>{NOMI_CATEGORIA[c.codiceCategoria] || c.codiceCategoria}</td>
                        <Numero>{euro(c.totale)}</Numero>
                        <Numero>{c.ordini} ordini</Numero>
                        <td style={{ textAlign: 'right' }}>
                          {c.stimati > 0 && <Badge $variant="warning">{c.stimati} stimati</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Tabella>
              </TabellaScorrevole>

              {!!esitoAmazon.categorieNonMappate.length && (
                <Alert $variant="warning">
                  Categorie Amazon senza una voce corrispondente, finite su “altre spese
                  operative”:{' '}
                  {esitoAmazon.categorieNonMappate.map((c) => `${c.categoria} (${c.righe})`).join(', ')}.
                </Alert>
              )}
            </>
          )}
        </CardBody>
      </Sezione>
    </>
  );
};

// ============================================================
// Riconciliazione
// ============================================================
const NOMI_CANALE: Record<string, string> = {
  contanti: 'Contanti',
  elettronico: 'Carte e POS',
  satispay: 'Satispay',
  delivery: 'Delivery',
  ticket: 'Ticket',
  assegni: 'Assegni',
  valute: 'Valute estere',
  non_riscosso: 'Non riscosso',
  altro: 'Altro'
};

const RiconciliazioneTab: React.FC = () => {
  const [periodo, setPeriodo] = useState(annoCorrente());
  const [dati, setDati] = useState<CeRiconciliazione | null>(null);
  const [caricamento, setCaricamento] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const carica = useCallback(async () => {
    setCaricamento(true);
    setErrore(null);
    try {
      setDati(await ApiService.getCeRiconciliazione(periodo.da, periodo.a));
    } catch (e: any) {
      setErrore(e?.response?.data?.error || 'Errore nella riconciliazione');
    } finally {
      setCaricamento(false);
    }
  }, [periodo]);

  useEffect(() => { carica(); }, [carica]);

  return (
    <>
      <Alert $variant="info">
        Confronta quanto è stato incassato secondo la cassa con quanto è arrivato davvero
        in banca. La differenza sull'elettronico è quello che trattengono i circuiti di
        pagamento: un costo che dal solo estratto conto non si vede. Il confronto è per
        periodo e non giorno per giorno, perché gli accrediti non seguono le vendite uno a
        uno.
      </Alert>

      <Filtri>
        <FormGroup>
          <Label htmlFor="ric-da">Dal</Label>
          <Input id="ric-da" type="date" value={periodo.da}
            onChange={(e) => setPeriodo({ ...periodo, da: e.target.value })} />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="ric-a">Al</Label>
          <Input id="ric-a" type="date" value={periodo.a}
            onChange={(e) => setPeriodo({ ...periodo, a: e.target.value })} />
        </FormGroup>
        <Button $variant="secondary" onClick={carica} disabled={caricamento}>
          <RefreshCw /> {caricamento ? 'Calcolo…' : 'Aggiorna'}
        </Button>
      </Filtri>

      {errore && <Alert $variant="error">{errore}</Alert>}

      {dati && (
        <>
          <Sezione>
            <CardHeader><h3>Costo reale dell'incasso elettronico</h3></CardHeader>
            <CardBody>
              <TabellaScorrevole>
                <Tabella>
                  <tbody>
                    <tr>
                      <td>Incassato secondo la cassa</td>
                      <Numero>{euro(dati.elettronico.incassatoKiara)}</Numero>
                    </tr>
                    <tr>
                      <td>Arrivato in banca</td>
                      <Numero>{euro(dati.elettronico.arrivatoInBanca)}</Numero>
                    </tr>
                    <RigaMargine $forte>
                      <td>Trattenuto dai circuiti</td>
                      <Numero $negativo>
                        {euro(dati.elettronico.differenza)} · {percentuale(dati.elettronico.costoPercentuale)}
                      </Numero>
                    </RigaMargine>
                  </tbody>
                </Tabella>
              </TabellaScorrevole>

              {!!dati.elettronico.perFornitore.length && (
                <>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)', margin: 'var(--space-lg) 0 var(--space-sm)' }}>
                    Accrediti per circuito
                  </div>
                  <TabellaScorrevole>
                    <Tabella>
                      <tbody>
                        {dati.elettronico.perFornitore.map((f, i) => (
                          <tr key={i}>
                            <td>{f.fornitore || 'Non attribuito'}</td>
                            <Numero>{euro(f.totale)}</Numero>
                            <Numero>{f.movimenti} movimenti</Numero>
                          </tr>
                        ))}
                      </tbody>
                    </Tabella>
                  </TabellaScorrevole>
                </>
              )}
            </CardBody>
          </Sezione>

          <Sezione>
            <CardHeader><h3>Per canale di incasso</h3></CardHeader>
            <CardBody>
              <TabellaScorrevole>
                <Tabella>
                  <thead>
                    <tr>
                      <th>Canale</th>
                      <th style={{ textAlign: 'right' }}>Cassa</th>
                      <th style={{ textAlign: 'right' }}>In banca</th>
                      <th style={{ textAlign: 'right' }}>Differenza</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dati.confronti.map((c) => (
                      <tr key={c.canale}>
                        <td>{NOMI_CANALE[c.canale] || c.canale}</td>
                        <Numero>{euro(c.incassatoKiara)}</Numero>
                        <Numero>{c.confrontabile ? euro(c.arrivatoInBanca) : '—'}</Numero>
                        <Numero $negativo={(c.differenza ?? 0) < 0}>
                          {c.confrontabile ? euro(c.differenza) : 'non confrontabile'}
                        </Numero>
                      </tr>
                    ))}
                  </tbody>
                </Tabella>
              </TabellaScorrevole>

              <Nota>
                I canali elettronici confluiscono tutti negli stessi accrediti bancari: la
                colonna “in banca” ripete quindi lo stesso totale, ed è il confronto complessivo
                qui sopra a essere significativo, non quello riga per riga.
              </Nota>
            </CardBody>
          </Sezione>

          <Sezione>
            <CardHeader><h3>Contanti</h3></CardHeader>
            <CardBody>
              <TabellaScorrevole>
                <Tabella>
                  <tbody>
                    <tr>
                      <td>Incassati secondo la cassa</td>
                      <Numero>{euro(dati.contanti.incassatoKiara)}</Numero>
                    </tr>
                    <tr>
                      <td>Versati in banca</td>
                      <Numero>{euro(dati.contanti.versatoInBanca)}</Numero>
                    </tr>
                    <RigaMargine>
                      <td>Differenza</td>
                      <Numero $negativo={dati.contanti.versatoInBanca - dati.contanti.incassatoKiara < 0}>
                        {euro(dati.contanti.versatoInBanca - dati.contanti.incassatoKiara)}
                      </Numero>
                    </RigaMargine>
                  </tbody>
                </Tabella>
              </TabellaScorrevole>
              <Nota>
                Sui contanti uno scostamento è fisiologico: si versa a mazzette quando si passa
                in banca, quindi gli incassi di fine periodo finiscono versati in quello dopo.
                Conta l'andamento su più mesi, non il saldo di un singolo periodo.
              </Nota>
            </CardBody>
          </Sezione>

          {!!dati.canaliNonMappati.length && (
            <Alert $variant="warning">
              Questi canali non hanno un corrispondente sul lato banca e non sono
              confrontabili:{' '}
              {dati.canaliNonMappati.map((c) => NOMI_CANALE[c.canale] || c.canale).join(', ')}.
            </Alert>
          )}
        </>
      )}
    </>
  );
};

// ============================================================
// Pagina
// ============================================================
type SchedaAttiva = 'conto-economico' | 'coda' | 'import' | 'fatture' | 'regole' | 'riconciliazione';

const ControlloGestionePage: React.FC = () => {
  const [scheda, setScheda] = useState<SchedaAttiva>('conto-economico');

  return (
    <PageContainerWide>
      <PageHeader title="Controllo di gestione" icon={<TrendingUp />} />

      <Tabs>
        <Tab $attiva={scheda === 'conto-economico'} onClick={() => setScheda('conto-economico')}>
          <TrendingUp size={16} /> Conto economico
        </Tab>
        <Tab $attiva={scheda === 'coda'} onClick={() => setScheda('coda')}>
          <ListChecks size={16} /> Da classificare
        </Tab>
        <Tab $attiva={scheda === 'import'} onClick={() => setScheda('import')}>
          <Upload size={16} /> Estratti conto
        </Tab>
        <Tab $attiva={scheda === 'fatture'} onClick={() => setScheda('fatture')}>
          <FileText size={16} /> Fatture
        </Tab>
        <Tab $attiva={scheda === 'regole'} onClick={() => setScheda('regole')}>
          <Wand2 size={16} /> Regole
        </Tab>
        <Tab $attiva={scheda === 'riconciliazione'} onClick={() => setScheda('riconciliazione')}>
          <Scale size={16} /> Riconciliazione
        </Tab>
      </Tabs>

      {scheda === 'conto-economico' && <ContoEconomicoTab />}
      {scheda === 'coda' && <CodaTab />}
      {scheda === 'import' && <ImportTab />}
      {scheda === 'fatture' && <FattureTab />}
      {scheda === 'regole' && <RegoleTab />}
      {scheda === 'riconciliazione' && <RiconciliazioneTab />}
    </PageContainerWide>
  );
};

export default ControlloGestionePage;
